import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import { useState } from "react";
import { useEditorStore } from "../../../lib/stores/editor-store/editor-store";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Toast from "@/lib/components/Toast";

const SUPPORTED_DOMAINS = ["rightmove.co.uk", "zillow.com"];

const getNormalizedUrl = (url: string): string => {
  let normalizedUrl = url.trim();
  if (
    !normalizedUrl.startsWith("http://") &&
    !normalizedUrl.startsWith("https://")
  ) {
    normalizedUrl = "https://" + normalizedUrl;
  }
  return normalizedUrl;
};

const formSchema = z.object({
  url: z
    .string()
    .min(1, "Please enter a valid URL")
    .refine((url) => {
      const normalizedUrl = getNormalizedUrl(url);

      return SUPPORTED_DOMAINS.some((domain) => normalizedUrl.includes(domain));
    }, `This real estate site is not supported yet.`),
});

type FetchRealEstateListingProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export default function FetchRealEstateListing({
  open,
  setOpen,
}: FetchRealEstateListingProps) {
  const [isFetching, setIsFetching] = useState(false);

  const fetchRealEstateListingImages = useEditorStore(
    (state) => state.service.fetchRealEstateListingImages,
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
    },
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsFetching(true);

    try {
      const normalizedUrl = getNormalizedUrl(values.url);
      await fetchRealEstateListingImages(normalizedUrl);

      setOpen(false);
      form.reset();
      Toast.info("Successfully fetched real estate listing images");
    } catch (error) {
      console.error(error);
      Toast.error("Failed to fetch real estate listing images");
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Import images from a listing site</DialogTitle>
          <DialogDescription>
            Paste a Zillow or Rightmove listing link and Amplifiles downloads
            its images automatically.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Listing URL</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      placeholder="https://www.zillow.com/homedetails/123-Main-St-Austin-TX-78701/12345678_zpid/"
                      disabled={isFetching}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-8">
              <Button
                type="submit"
                disabled={isFetching || !form.watch("url").trim()}
                isLoading={isFetching}
              >
                Get images
              </Button>
              <DialogClose asChild>
                <Button variant="outline" disabled={isFetching}>
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
